import assert from 'assert';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createStore } from 'zustand/vanilla';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../Interfaces/ILegalClient';

export type ILegalClientMap = Record<number, ILegalClient>;

type InternalState = {
  byId: ILegalClientMap;
  nextTempId: number; // negative temp IDs
};

type InternalActions = {
  createLegalClient: (legalClient?: Partial<ILegalClient>) => ILegalClient;
  updateLegalClient: (legalClient: Partial<ILegalClient>) => ILegalClient;
};

type ExternalState = {
  readonly legalClientArray: ILegalClient[];
};

type ExternalActions = {
  putLegalClient: (legalClient?: Partial<ILegalClient>) => ILegalClient;
  getLegalClient: (id: number) => ILegalClient | undefined;
  removeLegalClient: (id: number) => void;
};

export type LegalClientStore = ExternalState & ExternalActions;

const project = (byId: ILegalClientMap) => Object.values(byId);

export const legalClientStore = createStore<
  LegalClientStore & InternalState & InternalActions
>((set, get) => ({
  byId: {},
  nextTempId: -1,
  legalClientArray: [],

  createLegalClient: (legalClient = {}) => {
    const id = get().nextTempId;
    const client: ILegalClient = {
      ...getEmptyLegalClient(),
      ...legalClient,
      idLegalClient: id,
    } as ILegalClient;
    set(state => {
      const byId = { ...state.byId, [id]: client };
      return {
        byId,
        nextTempId: id - 1,
        legalClientArray: project(byId),
      };
    });
    return client;
  },

  updateLegalClient: (legalClient: Partial<ILegalClient>) => {
    const existingClient = get().getLegalClient(
      legalClient.idLegalClient!
    ) as ILegalClient;

    assert(existingClient, 'Cannot update non-existing legal client');

    const updatedClient = { ...existingClient, ...legalClient };
    set(state => {
      const byId = {
        ...state.byId,
        [legalClient.idLegalClient!]: updatedClient,
      };
      return {
        byId,
        legalClientArray: project(byId),
      };
    });
    return updatedClient;
  },

  getLegalClient: id => get().byId[id],

  putLegalClient: (legalClient = {}) => {
    const existingLegalClient = get().getLegalClient(
      legalClient.idLegalClient!
    );

    if (existingLegalClient) {
      return get().updateLegalClient(legalClient);
    }

    return get().createLegalClient(legalClient);
  },

  removeLegalClient: id => {
    set(state => {
      const { [id]: _, ...rest } = state.byId; // omit the client with the given id
      return { byId: rest, legalClientArray: project(rest) };
    });
  },
}));

legalClientStore.getInitialState().removeLegalClient(Number.MAX_SAFE_INTEGER);

export const useLegalClientStore = <T,>(
  selector: (s: LegalClientStore) => T,
  equality?: (a: T, b: T) => boolean
) => {
  if (
    typeof (legalClientStore as any)?.getState !== 'function' ||
    typeof (legalClientStore as any)?.subscribe !== 'function'
  ) {
    console.error('Invalid store passed to useStore:', legalClientStore);
    throw new Error('Provider/store value is not a Zustand StoreApi');
  }
  return useStoreWithEqualityFn(legalClientStore, selector, equality);
};

export { shallow };
